/**
 * AgGridTable - generic AG Grid wrapper for simple, server-paginated tables.
 *
 * Unlike PacsAgGrid (which is tightly coupled to the PACS study-list data shape,
 * with its own renderer/value-path maps, column configurator and pinning
 * persistence), this component is data-shape agnostic. It accepts plain
 * Ant-Design-style column definitions ({ title, dataIndex, key, width, fixed,
 * align, render, className }) and renders every cell through the column's own
 * `render` function, so existing column constants can be reused verbatim.
 *
 * It reuses PacsAgGrid.css so the visual styling matches the PACS list exactly.
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { Spin } from 'antd';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../PacsAgGrid/PacsAgGrid.css';

interface AgGridTableProps {
  /** Row data array */
  rowData: any[];
  /** Loading state - shows a centered spinner overlay */
  loading?: boolean;
  /** Ant-Design-style column definitions */
  columns: any[];
  /** Grid height (CSS value). Defaults to a viewport-relative height. */
  height?: string;
  /**
   * Row identity. A string is treated as a (dot-notation) path into the row;
   * a function receives the row and must return a stable id. When omitted,
   * AG Grid manages row identity internally.
   */
  rowKey?: string | ((record: any) => string | number | null | undefined);
  /** Called when a data row is clicked (ignores clicks on interactive elements) */
  onRowClick?: (record: any) => void;
  /** Default column width when a column has no explicit width */
  defaultColWidth?: number;
  /** Extra col-ids whose cells should NOT trigger onRowClick (status/action cols are ignored by default) */
  nonClickableColIds?: string[];
  /**
   * Optional per-row CSS class derived from the row data (e.g. to tint
   * emergency rows). Receives the raw record and returns a class name (or
   * empty string for none). The class must be defined in PacsAgGrid.css.
   */
  getRowClass?: (record: any) => string | string[] | undefined;
}

/** Column ids that are action/control columns - never sortable/filterable/movable. */
const ACTION_COL_IDS = new Set([
  'actions',
  'actions_column',
  'edit_study_details',
  'parent',
  'child',
  'notes_icon',
  'info_icon',
  'assigned_to_tech',
]);

/** Extract a plain-text header name from an antd column title (handles React elements). */
const extractHeaderName = (col: any, index: number): string => {
  if (typeof col.title === 'string') return col.title;
  const children = col.title?.props?.children;
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.filter((c: any) => typeof c === 'string').join('') || '';
  }
  return col.key || col.dataIndex || `Column ${index}`;
};

/** Read a nested value by dot-notation path. */
const getByPath = (obj: any, path?: string) => {
  if (!path || !obj) return undefined;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

/**
 * Cell renderer that defers to the column's own antd `render(value, record, index)`.
 * The render function reference is passed via cellRendererParams so the columnDefs
 * memo doesn't need to depend on it.
 */
const RenderCell = (params: any) => {
  const renderFn = params.colDef?.cellRendererParams?.renderFn;
  if (!renderFn) return params.value ?? '';
  try {
    return renderFn(params.value, params.data, params.rowIndex);
  } catch (err) {
    console.error('AgGridTable cell render error:', err);
    return '';
  }
};

const AgGridTable: React.FC<AgGridTableProps> = ({
  rowData = [],
  loading = false,
  columns = [],
  height = 'calc(100vh - 24rem)',
  rowKey,
  onRowClick,
  defaultColWidth = 150,
  nonClickableColIds = [],
  getRowClass,
}) => {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs: ColDef[] = useMemo(() => {
    return columns.map((col, index) => {
      const field = col.dataIndex || col.key || undefined;
      const colId = col.key || col.dataIndex || `col_${index}`;
      const isActionCol = ACTION_COL_IDS.has(colId);
      const isPinned = col.fixed === 'right' ? 'right' : col.fixed === 'left' ? 'left' : undefined;

      const agCol: ColDef = {
        headerName: extractHeaderName(col, index),
        field,
        colId,
        minWidth: col.minWidth ?? 60,
        resizable: true,
        sortable: !isActionCol && !!field,
        filter: !isActionCol && !!field,
        pinned: isPinned,
        suppressMovable: !!isPinned || isActionCol,
        cellClass: col.className,
      };

      // A column may flex to fill remaining width (col.flex) or use a fixed width.
      // Flex columns clamp at minWidth, so the table scrolls horizontally only
      // once the viewport is too narrow for everything (never squashes below min).
      if (col.flex) {
        agCol.flex = col.flex;
      } else {
        agCol.width = col.width || defaultColWidth;
      }

      if (col.align) {
        agCol.cellStyle = {
          justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
        };
      }

      if (typeof col.render === 'function') {
        agCol.cellRenderer = RenderCell;
        agCol.cellRendererParams = { renderFn: col.render };
      }

      return agCol;
    });
  }, [columns, defaultColWidth]);

  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    suppressMenu: false,
  }), []);

  const getRowId = useMemo(() => {
    if (!rowKey) return undefined;
    return (params: { data: any }) => {
      const id = typeof rowKey === 'function' ? rowKey(params.data) : getByPath(params.data, rowKey);
      return id != null ? String(id) : '';
    };
  }, [rowKey]);

  const ignoreColIds = useMemo(
    () => new Set<string>(['status', 'po_status', 'actions', 'actions_column', ...nonClickableColIds]),
    [nonClickableColIds]
  );

  const handleRowClicked = useCallback((event: RowClickedEvent) => {
    if (!onRowClick) return;
    const target = event.event?.target as HTMLElement | undefined;
    // Skip clicks on interactive elements (buttons, links, inputs, dropdowns, etc.)
    if (
      target?.closest(
        'button, .ant-btn, .anticon, a, input, .ant-select, .ant-tooltip, .ant-dropdown, .action-icon-button, .ant-popover, .ant-checkbox'
      )
    ) {
      return;
    }
    // Skip clicks landing in status / action columns
    const colId = target?.closest('.ag-cell')?.getAttribute('col-id') || '';
    if (ignoreColIds.has(colId)) return;
    onRowClick(event.data);
  }, [onRowClick, ignoreColIds]);

  const handleGetRowClass = useCallback(
    (params: { data: any }) => (getRowClass ? getRowClass(params.data) : ''),
    [getRowClass]
  );

  const onGridReady = useCallback((_params: GridReadyEvent) => {
    // no-op; kept for parity / future use
  }, []);

  return (
    <div className="pacs-ag-grid-container">
      {loading && (
        <div className="pacs-ag-grid-loading">
          <Spin size="large" />
        </div>
      )}
      <div
        className="ag-theme-alpine pacs-ag-grid ag-grid-table"
        style={{ width: '100%', height }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId as any}
          getRowClass={handleGetRowClass}
          onGridReady={onGridReady}
          onRowClicked={handleRowClicked}
          animateRows={false}
          enableCellTextSelection={true}
          suppressCellFocus={true}
          rowHeight={40}
          headerHeight={32}
          suppressRowVirtualisation={true}
          suppressColumnVirtualisation={true}
        />
      </div>
    </div>
  );
};

export default AgGridTable;
